import { useState, useEffect } from 'react';
import type { TimePeriod } from '../types';

interface FiltersProps {
  period: TimePeriod;
  onPeriodChange: (period: TimePeriod, startDate?: string, endDate?: string) => void;
  sortBy: string;
  onSortChange: (sortBy: string) => void;
  sortOptions: { value: string; label: string }[];
  customStartDate?: string;
  customEndDate?: string;
}

export function Filters({
  period,
  onPeriodChange,
  sortBy,
  onSortChange,
  sortOptions,
  customStartDate,
  customEndDate
}: FiltersProps) {
  const [showCustom, setShowCustom] = useState(period === 'custom');
  const [startDate, setStartDate] = useState(customStartDate || '');
  const [endDate, setEndDate] = useState(customEndDate || '');

  // Sync with props when period changes externally
  useEffect(() => {
    setShowCustom(period === 'custom');
    if (period === 'custom' && customStartDate && customEndDate) {
      setStartDate(customStartDate);
      setEndDate(customEndDate);
    }
  }, [period, customStartDate, customEndDate]);

  const handlePeriodSelect = (newPeriod: TimePeriod) => {
    if (newPeriod === 'custom') {
      setShowCustom(true);
      // Don't trigger change yet, wait for dates
    } else {
      setShowCustom(false);
      onPeriodChange(newPeriod);
    }
  };

  const applyCustomRange = () => {
    if (startDate && endDate) {
      onPeriodChange('custom', startDate, endDate);
    }
  };

  // Get today's date in YYYY-MM-DD format for max date
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="filters-section">
      <div className="filter-group">
        <label className="filter-label">Time Period</label>
        <select
          className="filter-control"
          value={showCustom ? 'custom' : period}
          onChange={(e) => handlePeriodSelect(e.target.value as TimePeriod)}
        >
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
          <option value="custom">Custom Range</option>
        </select>
      </div>

      {showCustom && (
        <div className="custom-date-range">
          <div className="date-inputs">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || today}
              className="date-input"
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              max={today}
              className="date-input"
            />
          </div>
          <button
            onClick={applyCustomRange}
            disabled={!startDate || !endDate}
            className="apply-dates-btn"
          >
            Apply
          </button>
        </div>
      )}

      <div className="filter-group">
        <label className="filter-label">Sort By</label>
        <select
          className="filter-control"
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
        >
          {sortOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
